import { useCallback, useState } from "react";

import { EditIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { useCaching } from "utils/common";

import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import IconButton from "components/IconButton";
import Text from "components/Text";
import TextField from "components/TextField";
import toast from "components/Toast";

import { updateMemoDealInfo } from "entities/Memo/sdk";

const EditDocumentAdditionalHeaderInfoButton = ({
  dealId,
  memoId,
  value: initialValue,
  onChange,
}: {
  dealId: number;
  memoId: number;
  value: string;
  onChange: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useCaching<string>(initialValue);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSave = useCallback(() => {
    updateMemoDealInfo({
      memoId,
      dealId,
      additionalHeaderInfo: value,
    })
      .then(() => {
        onChange();
        toast.successMessage(
          "The additional header info has been updated successfully!"
        );
      })
      .finally(() => {
        handleClose();
      });
  }, [memoId, dealId, value, onChange, handleClose]);

  const handleChange = useCallback(
    (event) => {
      setValue(event.target.value);
    },
    [setValue]
  );

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        data-testid="edit-additional-info-button"
      >
        <EditIcon />
      </IconButton>
      <Dialog
        onClose={handleClose}
        open={open}
        data-testid="edit-additional-info-dialog"
      >
        <DialogTitle
          sx={{
            gap: 1,
            margin: 2,
            padding: 2,
            borderRadius: "8px",
            backgroundColor: colors.blue10,
          }}
        >
          <Text variant="h2">Additional header info</Text>
          <Text variant="text2" color={colors.gray60}>
            You can use this field to include information in the memo header. It
            can be used for the date, project info, or any other details you’d
            like to include.
          </Text>
        </DialogTitle>
        <DialogContent sx={{ padding: theme.spacing(2, 2, 0, 2) }}>
          <TextField
            autoFocus
            defaultValue={value}
            onChange={handleChange}
            sx={{ width: "100%" }}
            data-testid="additional-info-input"
          />
        </DialogContent>

        <DialogActions sx={{ padding: 2 }}>
          <Button
            variant="text"
            onClick={handleClose}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button onClick={handleSave} data-testid="save-button">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditDocumentAdditionalHeaderInfoButton;
